import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import React, { FC, useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BorderedColumn, GreyBox, PageColumn, WaitFor } from '../../components/blocks';
import { useTranslation } from '../../components/blocks/localization';
import Button from '../../components/blocks/localization/Button';
import { FilterTags } from '../../components/composites';
import {
    CreatedDateRangeFilter,
    ExtractionSearchFilter,
    ExtractionStatusFilter,
} from '../../components/composites/ExtractionFilters';
import { PaginatedTable } from '../../components/constructed';
import { EXTRACTIONS_MAX_RESULTS, EXTRACTIONS_PER_PAGE } from '../../constants';
import {
    extractionListChangePage,
    extractionListFilterChange,
    extractionListSortChange,
    getExtractionList,
} from '../../redux/actions/extractionListActionCreators';
import { getResellerDetails } from '../../redux/actions/resellerActionCreators';
import { ExtractionSortDirection, RootState } from '../../redux/types';
import { SiteMapPage } from '../../types';
import { checkPermission } from '../../utils/security';
import { useSiteMapRouter } from '../../utils/SiteMapRouter';
import { createTableData, ExtractionRow } from './formatTableData';
import { useExtractionFilters } from './useExtractionFilters';

const ExtractionListPage: FC<ExtractionListPageProps> = ({
    resellerId,
    user,
    resellerDetails,
    listState,
    getResellerDetails,
    getExtractionList,
    extractionListChangePage,
    extractionListSortChange,
    extractionListFilterChange,
}) => {
    const { t } = useTranslation(['extractionListPage', 'extractionStatus', 'common']);
    const { getPath } = useSiteMapRouter();
    const timezone = resellerDetails.value?.resellerIanaTimezone || undefined;
    const canReviewDrafts = checkPermission([SiteMapPage.reviewPurchaseOrderExtraction], user, 'page') === 'allowed';

    const combinedLoadState = {
        ...listState.extractionData,
        status:
            listState.extractionData.status === 'Loaded' &&
            (resellerDetails.status === 'Uninitialized' || resellerDetails.status === 'Loading')
                ? ('Loading' as const)
                : listState.extractionData.status,
    };

    const { filters, filterTags, submitQuery, clearFilters } = useExtractionFilters({
        initialState: listState,
        onFilterChange: extractionListFilterChange,
    });

    useEffect(() => {
        if (resellerId && resellerDetails.status === 'Uninitialized') {
            getResellerDetails(resellerId);
        }
    }, [resellerId, resellerDetails.status, getResellerDetails]);

    useEffect(() => {
        if (resellerId && listState.extractionData.status === 'Uninitialized') {
            getExtractionList(resellerId, listState.startAt, EXTRACTIONS_MAX_RESULTS);
        }
    }, [resellerId, listState.extractionData.status, listState.startAt, getExtractionList]);

    const onSort = useCallback(
        ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: string }) => {
            submitQuery();
            extractionListSortChange(
                sortColumn,
                ExtractionSortDirection[sortDirection as keyof typeof ExtractionSortDirection]
            );
        },
        [extractionListSortChange, submitQuery]
    );

    const onPageChange = useCallback(
        (value: string) => {
            const page = parseInt(value);
            if (!page) return;
            // One request per page, so the offset is always an exact multiple of the page size.
            extractionListChangePage(page, EXTRACTIONS_PER_PAGE * (page - 1));
        },
        [extractionListChangePage]
    );

    const calculateNumberOfPages = (total: number) => Math.ceil(total / EXTRACTIONS_PER_PAGE);

    return (
        <PageColumn width={'100%'}>
            <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
                <GreyBox type={'fill'} width={'100%'} spacingInset={'small'}>
                    <Row alignmentHorizontal={'justify'} alignmentVertical={'center'}>
                        <Heading level={5} alignment={'left'}>
                            {t('extractions-pageTitle')}
                            {` (${listState.totalRecords})`}
                        </Heading>
                    </Row>
                </GreyBox>
                <WaitFor lazyValue={combinedLoadState}>
                    {(extractions) => (
                        <PaginatedTable<ExtractionRow>
                            layout={'stacked'}
                            filterAlignment={'center'}
                            inlineFilterActions={true}
                            data={createTableData(extractions, { getPath, t, canReviewDrafts, resellerId, timezone })}
                            dataTestId={'extractionsTable'}
                            currentPage={listState.currentPage}
                            numberOfPages={calculateNumberOfPages(listState.totalRecords)}
                            onPageChange={onPageChange}
                            onSort={onSort}
                            sortColumn={listState.sortColumn}
                            sortDirection={listState.sortDirection}
                            rowKey={(_, rowObject) => rowObject.extractionId}
                            searchButton={
                                <Button
                                    type={'primary'}
                                    onClick={submitQuery}
                                    size={'large'}
                                    data-testid={'extractionListPage.searchButton'}
                                >
                                    <p>{t('common:search-text')}</p>
                                </Button>
                            }
                            clearButton={
                                <Button
                                    type={'tertiary'}
                                    onClick={clearFilters}
                                    size={'large'}
                                    data-testid={'extractionListPage.clearButton'}
                                >
                                    <p>{t('common:clear-text')}</p>
                                </Button>
                            }
                            filterTags={<FilterTags tags={filterTags} t={t} />}
                        >
                            <ExtractionSearchFilter
                                searchBy={filters.searchBy.value}
                                onSearchByChange={filters.searchBy.onChange}
                                query={filters.query.value}
                                onQueryChange={filters.query.onChange}
                                onSubmit={submitQuery}
                                t={t}
                            />
                            <ExtractionStatusFilter
                                value={filters.status.value}
                                onChange={filters.status.onChange}
                                t={t}
                            />
                            <CreatedDateRangeFilter
                                value={filters.dateRange.value}
                                onChange={filters.dateRange.onChange}
                                t={t}
                                timezone={timezone}
                            />
                        </PaginatedTable>
                    )}
                </WaitFor>
            </BorderedColumn>
        </PageColumn>
    );
};

const mapStateToProps = ({ extractionListReducer, resellerReducer, userReducer }: RootState) => ({
    user: userReducer.user,
    resellerId: resellerReducer.selectedReseller.value?.resellerId,
    resellerDetails: resellerReducer.resellerDetails,
    listState: extractionListReducer,
});

const mapDispatchToProps = {
    getResellerDetails,
    getExtractionList,
    extractionListChangePage,
    extractionListSortChange,
    extractionListFilterChange,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type ExtractionListPageProps = ConnectedProps<typeof connector>;
export default connector(ExtractionListPage);
